import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { candidateService } from '../../lib/api/candidate';
import {
    CandidateAdvancedRequestType,
    CandidateGlobalRequestType,
    CandidateListType,
    OfferNextActionEnum,
} from '../../types/candidate';
import { history } from '../../history';
import { routes } from '../routes/routes-names';
import avatar from '../../assets/img/avatars.svg';
import { generateLink } from '../../lib/utils/generate-link';
import PageLoader from '../../components/loader';
import FAQImg from '../../assets/img/faq-search-icon.png';
import { SuspendEnum } from '../../types/suspension';
import { Rating } from 'react-simple-star-rating';
import { ExperienceLevelEnum } from '../../lib/constants/constants';
import { Helmet } from 'react-helmet';
import PhoneCodeDropdown from '../../components/dropdown/phoneCode';
import StateCityCodeValue from '../../components/profile/stateCityCodeValue';
import { useConditionalEffects } from '../../hooks/force-signout';
import { encodeProfileId } from '../claimed/search-box';
import { showWarning } from '../invitations/candidate';
import useUser from '../../hooks/useSelector';


export type ParamsType = {
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
    cityDistrict?: string;
    state?: string;
    country?: string;
    institutionName?: string;
    userCountryCode?: string;
    phone?: string;
    countryPhoneCode?: string;
    email?: string;
    start?: number;
    limit?: number;
    searchParam?: string;
    IsOfferRelease?: boolean;
};

export type FormDataType = {
    firstName: string;
    lastName: string;
    jobTitle: string;
    cityDistrict: string;
    state: string;
    country: string;
    institutionName: string;
    userCountryCode: string;
    phone: string;
    countryPhoneCode: string | number;
    email: string;
    start: number;
    limit: number;
    [key: string]: string | number;
};

interface Country {
    CountryId: string;
    CountryName: string;
    CountryPhoneCode: string;
}

interface State {
    StateId: string;
    StateName: string;
    CountryId: string;
}

interface City {
    CitiesId: string;
    CitiesName: string;
    CitiesStateId: string;
}

export const defaultFormData = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    country: '',
    state: '',
    cityDistrict: '',
    institutionName: '',
    userCountryCode: '',
    phone: '',
    countryPhoneCode: '',
    email: '',
    start: 1,
    limit: 10,
};

const CandidatesSearchList: React.FC = () => {
    const location = useLocation();
    const queryParams: ParamsType = queryString.parse(location.search);
    const signOutServiceComponent = useConditionalEffects();

    const hasUser = useUser();

    const navigate = useNavigate();

    const {
        searchParam,
        firstName,
        lastName,
        jobTitle,
        country,
        cityDistrict,
        state,
        institutionName,
        userCountryCode,
        phone,
        countryPhoneCode,
        email,
        start,
        limit,
    } = queryParams;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<{
        items: CandidateListType[];
        count: number;
    }>({ items: [], count: 0 });

    const [formData, setFormData] = useState<FormDataType>(defaultFormData);

    const isAnyValuePresent = Object.keys(formData)
        .filter((key) => key !== 'start' && key !== 'limit')
        .some((key) => {
            const value = (formData as FormDataType)[key];
            return typeof value === 'string' ? value.trim() !== '' : value !== 0;
        });

    useEffect(() => {
        (async function () {
            setFormData(createFormData(queryParams));

            const isStartAndLimitValuePresent = Object.keys(queryParams)
                .filter((key) => key !== 'start' && key !== 'limit')
                .some((key) => {
                    const value = (queryParams as FormDataType)[key];
                    return typeof value === 'string' ? value.trim() !== '' : value !== 0;
                });

            try {
                setLoading(true);
                if (Object.keys(queryParams).length !== 0 && isStartAndLimitValuePresent) {
                    const {
                        resultObject: { items, count },
                    } = searchParam
                        ? await candidateService.getList(createGlobalSearchParams(queryParams))
                        : await candidateService.getListWithAnd(createAdvancedSearchParams(queryParams));

                    setData({ items, count });
                } else {
                    setData({ items: [], count: 0 });
                }
            } catch (err) {
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchParam,
        firstName,
        country,
        lastName,
        jobTitle,
        cityDistrict,
        state,
        institutionName,
        userCountryCode,
        phone,
        countryPhoneCode,
        email,
        start,
        limit,
    ]);

    useEffect(() => {
        signOutServiceComponent();
    }, [limit, start]);

    return (
        <>
            <Helmet>
                <title>OfferX - Candidates | No More Candidate Ghosting </title>
                <meta
                    name="description"
                    content="Discover a diverse pool of talented candidates on the only platform that also tackles offer blackouts - OfferX. It is easier to the perfect fit for your company by exploring our extensive database of skilled professionals."
                />
            </Helmet>
            <div className="company-page-contener">
                <div className="container-fluid">
                    <div className="row my-4">
                        <div className="col text-start">
                            <h1 className="fw-700 fs-20 mb-0">Advanced search</h1>
                        </div>
                    </div>
                    <div className="border-bottom mb-4 "></div>
                    <div className="row">
                        <div className="col-md-4 col-12 position-relative border-right">
                            <div className="card sticky">
                                <div className="card-body ps-0 pt-0">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <h1 className="fs-18 fw-700 mb-0">Search filters</h1>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col-6 col-md-12 col-lg">
                                            <label htmlFor="firstName" className="fw-700 mb-1 fs-13">
                                                First name
                                            </label>
                                            <input
                                                id="firstName"
                                                type="text"
                                                value={formData.firstName}
                                                onChange={(ev) => {
                                                    const regex = /^[a-zA-Z0-9 ]*$/;

                                                    if (regex.test(ev.target.value)) {
                                                        setFormData({
                                                            ...formData,
                                                            firstName: ev.target.value,
                                                        });
                                                    }
                                                }}
                                                className="form-control form-control-sm"
                                                placeholder="Enter first name"
                                            />
                                        </div>
                                        <div className="col-6 col-md-12 col-lg">
                                            <label htmlFor="lastName" className="fw-700 mb-1 fs-13">
                                                Last name
                                            </label>
                                            <input
                                                id="lastName"
                                                type="text"
                                                value={formData.lastName}
                                                onChange={(ev) => {
                                                    const regex = /^[a-zA-Z0-9 ]*$/;

                                                    if (regex.test(ev.target.value)) {
                                                        setFormData({
                                                            ...formData,
                                                            lastName: ev.target.value,
                                                        });
                                                    }
                                                }}
                                                className="form-control form-control-sm"
                                                placeholder="Enter last name"
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <label htmlFor="jobTitle" className="fw-700 mb-1 fs-13">
                                                Job title
                                            </label>
                                            <input
                                                id="jobTitle"
                                                type="text"
                                                value={formData.jobTitle}
                                                onChange={(ev) =>
                                                    setFormData({
                                                        ...formData,
                                                        jobTitle: ev.target.value,
                                                    })
                                                }
                                                className="form-control form-control-sm"
                                                placeholder="Enter job title"
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <label htmlFor="phone" className="fw-700 mb-1 fs-13">
                                                Contact number
                                            </label>
                                            <div className="d-flex">
                                                <PhoneCodeDropdown
                                                    id="advance-candidate-search"
                                                    advanceCandidateValue={formData.userCountryCode}
                                                    setFormData={setFormData}
                                                />
                                                <input
                                                    id="phone"
                                                    type="text"
                                                    value={formData.phone}
                                                    onChange={(ev) => {
                                                        const regex = /^\d+$/;

                                                        if (regex.test(ev.target.value)) {
                                                            setFormData({
                                                                ...formData,
                                                                phone: ev.target.value,
                                                            });
                                                        } else if (ev.target.value === '') {
                                                            setFormData({
                                                                ...formData,
                                                                phone: '',
                                                            });
                                                        }
                                                    }}
                                                    maxLength={10}
                                                    className="form-control form-phone-border form-control-sm"
                                                    placeholder="Enter contact number"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <label htmlFor="email" className="fw-700 mb-1 fs-13">
                                                Email
                                            </label>
                                            <input
                                                id="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(ev) =>
                                                    setFormData({
                                                        ...formData,
                                                        email: ev.target.value,
                                                    })
                                                }
                                                className="form-control form-control-sm"
                                                placeholder="Enter email"
                                            />
                                        </div>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col">
                                            <label htmlFor="institutionName" className="fw-700 mb-1 fs-13">
                                                Institution name
                                            </label>
                                            <input
                                                id="institutionName"
                                                type="text"
                                                value={formData.institutionName}
                                                onChange={(ev) =>
                                                    setFormData({
                                                        ...formData,
                                                        institutionName: ev.target.value,
                                                    })
                                                }
                                                className="form-control form-control-sm"
                                                placeholder="Enter institution name "
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col">
                                            {firstName ||
                                            lastName ||
                                            jobTitle ||
                                            cityDistrict ||
                                            country ||
                                            state ||
                                            institutionName ||
                                            userCountryCode ||
                                            phone ||
                                            countryPhoneCode ||
                                            email ? (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setParams({ ...defaultFormData, start: 0, limit: 0 }, navigate);
                                                        signOutServiceComponent();
                                                    }}
                                                    className="btn btn-outline-primary outline-p-hover w-100"
                                                >
                                                    Reset
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled
                                                    className="btn btn-outline-primary w-100"
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                        <div className="col">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newOptions = {
                                                        ...formData,
                                                        start: defaultFormData.start,
                                                        limit: defaultFormData.limit,
                                                    };
                                                    setParams(newOptions, navigate);
                                                    signOutServiceComponent();
                                                }}
                                                className="btn btn-primary w-100"
                                                disabled={!isAnyValuePresent}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-8 col-12 mt-4 mt-md-0">
                            <div className="card mb-3">
                                <div className="card-body pe-0 pt-0">
                                    <h1 className="fs-18 fw-700 mb-3">Candidates</h1>

                                    {loading ? (
                                        <PageLoader />
                                    ) : data.items.length !== 0 ? (
                                        <>
                                            {data.items.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="d-sm-flex align-items-center li-review position-relative search-record"
                                                >
                                                    <div className="d-flex align-items-center flex-grow-1">
                                                        <Link
                                                            to={generateLink(routes.candidate, {
                                                                privateSlug: `${
                                                                    item.firstName?.replace(/\s+/g, '') || 'NA'
                                                                }-${
                                                                    item.lastName?.replace(/\s+/g, '') || 'NA'
                                                                }-${encodeProfileId(item.id)}`,
                                                            })}
                                                            state={
                                                                item?.isUser === 1
                                                                    ? {
                                                                          data: {
                                                                              avatarUrl: item?.avatarUrl,
                                                                              gender: item?.gender,
                                                                              firstName: item?.firstName || '',
                                                                              middleName: item?.middleName || '',
                                                                              lastName: item?.lastName || '',
                                                                              email: item?.email,
                                                                              jobtitle: item?.jobTitle,
                                                                              country: item?.country,
                                                                              candidateLocation:
                                                                                  item?.candidateLocation,
                                                                              cityDistrict: item?.cityDistrict,
                                                                              state: item?.state,
                                                                              experienceLevel: item?.experienceLevel,
                                                                          },
                                                                      }
                                                                    : undefined
                                                            }
                                                            className="absolute-link"
                                                        />
                                                        <div className="flex-shrink-0">
                                                            <img
                                                                src={item.avatarUrl || avatar}
                                                                alt=""
                                                                className="avatar avatar--sm"
                                                            />
                                                        </div>
                                                        <div className="flex-grow-1 ms-3">
                                                            <h1 className="fs-14 fw-700 text-capitalize mb-0">
                                                                {item?.firstName?.toLowerCase()}{' '}
                                                                {item?.lastName?.toLowerCase()}
                                                            </h1>
                                                            <div className={`${item?.isUser === 1 && 'blurred-div'}`}>
                                                                <Rating
                                                                    allowFraction={true}
                                                                    size={15}
                                                                    fillColor="#4EB6FF"
                                                                    initialValue={item?.isUser === 1 ? 0 : item.rating}
                                                                    transition={true}
                                                                    emptyColor="transparent"
                                                                    SVGstrokeColor="#4EB6FF"
                                                                    SVGstorkeWidth="1"
                                                                    readonly={true}
                                                                    allowTitleTag={false}
                                                                />
                                                                <span className="fs-12 ms-1">
                                                                    {item.reviewsCount} review
                                                                    {item.reviewsCount != 1 && 's'}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <div className="fs-12 text-capitalize">
                                                                    {item.experienceLevel ==
                                                                    ExperienceLevelEnum.Experienced
                                                                        ? item.jobTitle
                                                                        : ExperienceLevelEnum[
                                                                              ExperienceLevelEnum.Fresher
                                                                          ]}
                                                                </div>
                                                                <i className="bi bi-dot fs-14 mx-1"></i>
                                                                <div className="fs-12 text-capitalize">
                                                                    {item?.isUser === 1 ? (
                                                                        <span className="">
                                                                            {item?.candidateLocation}
                                                                        </span>
                                                                    ) : (
                                                                        <StateCityCodeValue
                                                                            id={item.id}
                                                                            countryValue={item.country}
                                                                            cityValue={item.cityDistrict}
                                                                            stateValue={item.state}
                                                                        />
                                                                    )}
                                                                    
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="flex-shrink-0 position-relative text-center lt-search-results"
                                                        style={{ zIndex: 2 }}
                                                    >
                                                        {(item.suspendStatus === SuspendEnum.active ||
                                                            item.suspendedStatus === SuspendEnum.active) &&
                                                            (!!item.allCompaniesAcceptedOffersCount ||
                                                                item.currentCompanyLastEventShortText) && (
                                                                <div className="py-2">
                                                                    {!!item.allCompaniesAcceptedOffersCount && (
                                                                        <span className="fw-600 fs-12 d-block">
                                                                            <span className="lt-tooltip">
                                                                                <span className="lt-tooltiptext fw-400 fs-12">
                                                                                    It displays the total number of
                                                                                    accepted offers of this candidate in
                                                                                    OfferX.
                                                                                </span>
                                                                                <i className="bi bi-info-circle lt-text-warning" />
                                                                            </span>
                                                                            <span className="lt-text-warning">
                                                                                {item.allCompaniesAcceptedOffersCount}{' '}
                                                                                accepted offer
                                                                                {item.allCompaniesAcceptedOffersCount >
                                                                                    1 && 's'}
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                    {item.currentCompanyLastEventShortText && (
                                                                        <span className="fw-600 fs-12 d-block">
                                                                            {item.currentCompanyLastEventDetailedText && (
                                                                                <span className="lt-tooltip">
                                                                                    <span className="lt-tooltiptext fw-400 fs-12">
                                                                                        {item.currentCompanyLastEventDetailedText.substring(
                                                                                            0,
                                                                                            60
                                                                                        )}
                                                                                    </span>
                                                                                    <i className="bi bi-info-circle text-primary" />
                                                                                </span>
                                                                            )}
                                                                            <span className="text-primary">
                                                                                {item.currentCompanyLastEventShortText}
                                                                            </span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}

                                                        {item.suspendStatus === SuspendEnum.active ||
                                                        item.suspendStatus === SuspendEnum.partialactive ||
                                                        item.suspendedStatus === SuspendEnum.active ||
                                                        item.suspendedStatus === SuspendEnum.partialactive ? (
                                                            <>
                                                                {item?.isUser !== 1 ? (
                                                                    <div className="lt-tooltip">
                                                                        {item.currentCompanyOfferNextAvailableAction ===
                                                                            OfferNextActionEnum.noActionAvailable && (
                                                                            <span className="lt-tooltiptext lt-tooltiptext--bottom fs-12">
                                                                                There is already an accepted offer for
                                                                                this candidate and your company.
                                                                            </span>
                                                                        )}
                                                                        {(hasUser && hasUser?.suspendedStatus === 0) ||
                                                                        (hasUser && hasUser?.suspendedStatus === 6) ? (
                                                                            <Link
                                                                                to={queryString.stringifyUrl({
                                                                                    url: routes.releaseOffer,
                                                                                    query: {
                                                                                        candidate: encodeProfileId(
                                                                                            item.id
                                                                                        ),
                                                                                        first: item?.firstName.replace(
                                                                                            /\s+/g,
                                                                                            ''
                                                                                        ),
                                                                                        last: item?.lastName.replace(
                                                                                            /\s+/g,
                                                                                            ''
                                                                                        ),
                                                                                        ...(item.reOfferId && {
                                                                                            offerId: item.reOfferId,
                                                                                        }),
                                                                                    },
                                                                                })}
                                                                                className={`btn btn-sm btn-outline-primary ox-offer-btn ${
                                                                                    item.currentCompanyOfferNextAvailableAction ===
                                                                                    OfferNextActionEnum.noActionAvailable
                                                                                        ? 'btn-disabled disabled'
                                                                                        : ''
                                                                                }`}
                                                                            >
                                                                                {item.currentCompanyOfferNextAvailableAction ===
                                                                                OfferNextActionEnum.reOffer ? (
                                                                                    <span>
                                                                                        <i className="bi bi-pencil"></i>{' '}
                                                                                        Revise offer
                                                                                    </span>
                                                                                ) : (
                                                                                    <span>
                                                                                        <i className="bi bi-file-earmark-text"></i>{' '}
                                                                                        Release an offer
                                                                                    </span>
                                                                                )}
                                                                            </Link>
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => showWarning(hasUser)}
                                                                                className={`btn btn-sm btn-outline-primary ox-offer-btn ${
                                                                                    item.currentCompanyOfferNextAvailableAction ===
                                                                                    OfferNextActionEnum.noActionAvailable
                                                                                        ? 'btn-disabled disabled'
                                                                                        : ''
                                                                                }`}
                                                                            >
                                                                                {item.currentCompanyOfferNextAvailableAction ===
                                                                                OfferNextActionEnum.reOffer ? (
                                                                                    <span>
                                                                                        <i className="bi bi-pencil"></i>{' '}
                                                                                        Revise offer
                                                                                    </span>
                                                                                ) : (
                                                                                    <span>
                                                                                        <i className="bi bi-file-earmark-text"></i>{' '}
                                                                                        Release an offer
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (hasUser && hasUser?.suspendedStatus === 0) ||
                                                                  (hasUser && hasUser?.suspendedStatus === 6) ? (
                                                                    
                                                                    <Link
                                                                        to={generateLink(routes.candidate, {
                                                                            privateSlug: `${
                                                                                item.firstName?.replace(/\s+/g, '') ||
                                                                                'NA'
                                                                            }-${
                                                                                item.lastName?.replace(/\s+/g, '') ||
                                                                                'NA'
                                                                            }-${encodeProfileId(item.id)}`,
                                                                            
                                                                        })}
                                                                        state={{
                                                                            data: {
                                                                                avatarUrl: item?.avatarUrl,
                                                                                gender: item?.gender,
                                                                                firstName: item?.firstName || '',
                                                                                middleName: item?.middleName || '',
                                                                                lastName: item?.lastName || '',
                                                                                email: item?.email,
                                                                                jobtitle: item?.jobTitle,
                                                                                country: item?.country,
                                                                                candidateLocation:
                                                                                    item?.candidateLocation,
                                                                                cityDistrict: item?.cityDistrict,
                                                                                state: item?.state,
                                                                                experienceLevel: item?.experienceLevel,
                                                                            },
                                                                        }}
                                                                        className="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        Send Invite
                                                                    </Link>
                                                                ) : (
                                                                    <div
                                                                        onClick={() => showWarning(hasUser)}
                                                                        className={`btn btn-sm btn-outline-primary `}
                                                                    >
                                                                        Send Invite
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className="text-danger fs-12">Account suspended</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="row mt-3">
                                                <div className="col-12 col-sm">
                                                    <div className="d-flex justify-content-start align-items-center">
                                                        <label className="w-auto">Items per page:</label>
                                                        <select
                                                            value={String(formData.limit)}
                                                            onChange={(ev) => {
                                                                const newOptions = {
                                                                    ...formData,
                                                                    limit: Number(ev.target.value),
                                                                    start: 1,
                                                                };
                                                                setParams(newOptions, navigate, searchParam);
                                                            }}
                                                            className="items-selecter"
                                                        >
                                                            <option value="10">10</option>
                                                            <option value="15">15</option>
                                                            <option value="25">25</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-sm text-sm-end mt-2 mt-sm-0">
                                                    <div className="d-flex justify-content-start align-items-center">
                                                        <label htmlFor="">
                                                            Page {Math.ceil(formData.start / formData.limit)} of{' '}
                                                            {Math.ceil(data.count / formData.limit)}
                                                        </label>
                                                        {Math.ceil(formData.start / formData.limit) > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newOptions = {
                                                                        ...formData,
                                                                        start: formData.start - formData.limit,
                                                                    };
                                                                    setParams(newOptions, navigate, searchParam);
                                                                }}
                                                                className="lt-action-btn"
                                                            >
                                                                <i className="bi bi-chevron-left" />
                                                            </button>
                                                        )}
                                                        {formData.start + formData.limit <= data.count && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newOptions = {
                                                                        ...formData,
                                                                        start: formData.start + formData.limit,
                                                                    };
                                                                    setParams(newOptions, navigate, searchParam);
                                                                }}
                                                                className="lt-action-btn ms-1"
                                                            >
                                                                <i className="bi bi-chevron-right" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center my-5 py-5">
                                            <div className="text-center">
                                                <img
                                                    src={FAQImg}
                                                    alt="We could not find results for your search"
                                                    className="ox-no-search-img"
                                                />
                                                <p className="fs-14">
                                                    We could not find results for your search. Please try another
                                                    search.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CandidatesSearchList;

export function createFormData(params: ParamsType): FormDataType {
    return {
        ...defaultFormData,
        ...(params.firstName && { firstName: params.firstName }),
        ...(params.lastName && { lastName: params.lastName }),
        ...(params.jobTitle && { jobTitle: params.jobTitle }),
        ...(params.cityDistrict && { cityDistrict: params.cityDistrict }),
        ...(params.state && { state: params.state }),
        ...(params.country && { country: params.country }),
        ...(params.institutionName && { institutionName: params.institutionName }),
        ...(params.userCountryCode && { userCountryCode: params.userCountryCode }),
        ...(params.phone && { phone: params.phone }),
        ...(params.countryPhoneCode && { countryPhoneCode: params.countryPhoneCode }),
        ...(params.email && { email: params.email }),
        ...(params.start && { start: Number(params.start) }),
        ...(params.limit && { limit: Number(params.limit) }),
    };
}

export function createGlobalSearchParams(params: ParamsType): CandidateGlobalRequestType {
    return {
        Start: Number(params.start) || defaultFormData.start,
        Limit: Number(params.limit) || defaultFormData.limit,
        'SearchParam.FilterValue': params.searchParam as string,
        IsOfferRelease: params.IsOfferRelease as boolean,
    };
}

export function createAdvancedSearchParams(params: ParamsType): CandidateAdvancedRequestType {
    return {
        Start: Number(params.start) || defaultFormData.start,
        Limit: Number(params.limit) || defaultFormData.limit,
        ...(params.firstName && { 'FirstName.FilterValue': params.firstName }),
        ...(params.lastName && { 'LastName.FilterValue': params.lastName }),
        ...(params.jobTitle && { 'JobTitle.FilterValue': params.jobTitle }),
        ...(params.cityDistrict && { 'CityDistrict.FilterValue': params.cityDistrict }),
        ...(params.state && { 'State.FilterValue': params.state }),
        ...(params.country && { 'Country.FilterValue': params.country }),
        ...(params.institutionName && { 'InstitutionName.FilterValue': params.institutionName }),
        ...(params.userCountryCode && { 'UserCountryCode.FilterValue': params.userCountryCode }),
        ...(params.phone && { 'Phone.FilterValue': params.phone }),
        ...(params.countryPhoneCode && { 'CountryPhoneCode.FilterValue': params.countryPhoneCode }),
        ...(params.email && { 'Email.FilterValue': params.email }),
    };
}

function setParams(options: FormDataType, navigate: ReturnType<typeof useNavigate>, searchParam?: string) {
    const search = {
        start: options.start,
        limit: options.limit,
        ...(searchParam
            ? {
                  searchParam,
              }
            : {
                  ...(options.firstName && { firstName: options.firstName }),
                  ...(options.lastName && { lastName: options.lastName }),
                  ...(options.jobTitle && { jobTitle: options.jobTitle }),
                  ...(options.cityDistrict && { cityDistrict: options.cityDistrict }),
                  ...(options.state && { state: options.state }),
                  ...(options.country && { country: options.country }),
                  ...(options.institutionName && { institutionName: options.institutionName }),
                  ...(options.userCountryCode && { userCountryCode: options.userCountryCode }),
                  ...(options.phone && { phone: options.phone }),
                  ...(options.countryPhoneCode && { countryPhoneCode: options.countryPhoneCode }),
                  ...(options.email && { email: options.email }),
              }),
    };
    navigate({ search: queryString.stringify(search) });
}
